// Mirrors backend MineTrace.Shared.Contracts.MiningRights.RestrictionCheckSummaryDto

export const RestrictedZoneSource = {
  Ingemmet: 1,
  Sernanp: 2,
  CulturaSigda: 3,
} as const
export type RestrictedZoneSourceValue = (typeof RestrictedZoneSource)[keyof typeof RestrictedZoneSource]

export const RestrictionClass = {
  Referential: 1,
  HighSensitivity: 2,
  SectoralValidationRequired: 3,
  OperationalMining: 4,
  HardStop: 5,
} as const
export type RestrictionClassValue = (typeof RestrictionClass)[keyof typeof RestrictionClass]

export interface RestrictionIntersectionItem {
  intersectionId: string
  restrictedZoneId: string
  source: RestrictedZoneSourceValue
  zoneName: string
  category: string | null
  norma: string | null
  entidad: string | null
  fechaPublicacion: string | null
  restrictionClass: RestrictionClassValue
  isReferential: boolean
  intersectionAreaM2: number
  overlapPercentage: number
  sourceUrl: string | null
}

export interface RestrictionCheckSummary {
  checkId: string
  checkedAt: string
  /**
   * El chequeo se corrió sobre una geometría distinta a la vigente: lo que describe es un
   * polígono que ya no existe. Hay que advertirlo, no presentarlo como resultado actual.
   */
  isStale: boolean
  totalIntersectingZones: number
  highestClass: RestrictionClassValue | null
  hasHardStop: boolean
  hasOperationalRestriction: boolean
  hasSectoralValidation: boolean
  hasSensitivity: boolean
  intersections: RestrictionIntersectionItem[]
}

export interface UpdatePolygonResponseBody {
  miningRightId: string
  restrictionCheck: RestrictionCheckSummary | null
}

export const sourceLabel = (src: RestrictedZoneSourceValue): string => {
  switch (src) {
    case RestrictedZoneSource.Ingemmet:
      return 'INGEMMET'
    case RestrictedZoneSource.Sernanp:
      return 'SERNANP'
    case RestrictedZoneSource.CulturaSigda:
      return 'Min. Cultura (SIGDA)'
  }
}

export const restrictionClassLabel = (cls: RestrictionClassValue): string => {
  switch (cls) {
    case RestrictionClass.HardStop:
      return 'Prohibición'
    case RestrictionClass.OperationalMining:
      return 'Restricción operativa'
    case RestrictionClass.SectoralValidationRequired:
      return 'Validación sectorial'
    case RestrictionClass.HighSensitivity:
      return 'Sensibilidad alta'
    case RestrictionClass.Referential:
      return 'Referencial'
  }
}

// Colores siguen la paleta del proyecto (Chakra colorPalette tokens).
export const restrictionClassColor = (cls: RestrictionClassValue): string => {
  switch (cls) {
    case RestrictionClass.HardStop:
      return 'red'
    case RestrictionClass.OperationalMining:
      return 'orange'
    case RestrictionClass.SectoralValidationRequired:
      return 'yellow'
    case RestrictionClass.HighSensitivity:
      return 'amber'
    case RestrictionClass.Referential:
      return 'gray'
  }
}

/** Mirrors MineTrace.Shared.Contracts.MiningRights.IngemmetImportResultDto */
export interface IngemmetImportResult {
  /** False = el código no existe en INGEMMET: código mal cargado, no fallo del servicio. */
  found: boolean
  /** Se trajo el contorno del DERECHO MINERO completo. Es lo normal en toda importación. */
  officialBoundaryImported: boolean
  /**
   * Además se copió ese contorno a la CONCESIÓN del minero. Solo cuando el usuario lo
   * pide: el derecho es el edificio y la concesión del minero su departamento dentro.
   */
  boundaryImported: boolean
  /** El minero ya tenía dibujada su concesión y se respetó. */
  boundaryKeptManual: boolean
  officialName: string | null
  concessionHolderName: string | null
  /** Null = no se pudo comparar. False = el minero NO es el titular, que es lo habitual. */
  concessionHolderIsSupplier: boolean | null
  ingemmetStatus: number | null
  ingemmetStatusDetail: string | null
  officialHectares: number | null
  message: string | null
}

/** Mirrors MineTrace.Shared.Contracts.MiningRights.IngemmetExpedienteStatusDto */
export const IngemmetDownloadStatus = {
  Pendiente: 1,
  Descargando: 2,
  Ensamblando: 3,
  Completado: 4,
  Fallido: 5,
} as const

export interface IngemmetExpedienteStatus {
  status: number
  totalPages: number
  downloadedPages: number
  pdfSizeBytes: number | null
  pdfStoragePath: string | null
  /** Motivo del fallo, o las páginas que INGEMMET no entregó. Puede venir con la descarga OK. */
  message: string | null
}
